import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { IconButton, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { fToNow } from 'src/utils/format-time';
import {
  getApplicationNotificationMessage,
  getConnectionNotificationMessage,
  getInterviewNotificationMessage,
  getInterviewReviewNotificationMessage,
  getJobInviteNotificationMessage,
  getJobLinkRequestNotificationMessage,
} from 'src/utils/notifications.utils';

import { useTranslate } from 'src/locales';
import { isAdmin, isOrganisationTenant } from 'src/services/auth/auth.utils';
import { useActionConnectionRequestMutation } from 'src/services/connections/connections.service';
import { ConnectionActionOption } from 'src/services/connections/connections.types';
import { useMarkAsReadMutation } from 'src/services/notifications/notifications.service';
import {
  Notification,
  NotificationCategory,
  NotificationType,
} from 'src/services/notifications/notifications.types';
import { DueMarker } from 'src/services/tasks/tasks.types';
import {
  setShowActionStageRequestModal,
  setShowOfferModal,
  showCandidateOfferModal,
} from 'src/store/slices/applications/applicationsSlice';
import { toggleCandidateInviteListModal, toggleCompanyInviteListModal } from 'src/store/slices/candidate/candidateSlice';
import { showConnectionActionModal } from 'src/store/slices/connections/connectionsSlice';
import { openInterviewActionModal } from 'src/store/slices/interviews/interviewSlice';
import {
  showCandidateJobRequestsModal,
  showJobLinkActionModal,
  showJobVerificationModal,
} from 'src/store/slices/jobs/jobsSlice';
import { useAppDispatch } from 'src/store/store';

import Iconify from 'src/components/iconify';
import { openCalendarEventViewer } from 'src/store/slices/app/appSlice';
import { TenantType } from 'src/types/enums';
import { BackgroundTaskType } from 'src/services/system/system.types';

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: Notification;
  closeDrawer: VoidFunction;
};

// const getNotificationTitle = (notification: Notification) => {
//   if (notification.category === NotificationCategory.JOB) {
//     return i18n.t()
//   }
// }

const getNotificationSubtitle = (notification: Notification) => {
  if (notification.category === NotificationCategory.CONNECTION) {
    return getConnectionNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.JOB) {
    return getJobLinkRequestNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.JOB_INVITE) {
    return getJobInviteNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.JOB_APPLICATION) {
    return getApplicationNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.INTERVIEW) {
    return getInterviewNotificationMessage(notification);
  }

  if (notification.type === NotificationType.INTERVIEW_IN_REVIEW) {
    return getInterviewReviewNotificationMessage(notification);
  }

  return notification.metadata?.body ?? 'New Notification';
};

export default function NotificationItem({ notification, closeDrawer }: NotificationItemProps) {
  console.log(notification);
  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const router = useRouter();

  const [actionConnectionRequest, { isLoading: isRejectingConnection }] =
    useActionConnectionRequestMutation();

  const [markAsRead] = useMarkAsReadMutation();

  const onAcceptConnectionRequest = useCallback(
    (connectionId: string) => {
      dispatch(
        showConnectionActionModal({ requestId: connectionId, notificationId: notification.id })
      );

      closeDrawer();
    },
    [closeDrawer, dispatch, notification.id]
  );

  const onRejectConnectionRequest = useCallback(
    async (connectionId: string) => {
      try {
        await actionConnectionRequest({
          connectionId,
          action: {
            action: ConnectionActionOption.Reject,
          },
        }).unwrap();

        closeDrawer();
        markAsRead({ notifications: [notification.id] });
        enqueueSnackbar(t(`admin-connections.api.action.success-${ConnectionActionOption.Reject}`));
      } catch (e) {
        console.error(e);

        enqueueSnackbar(t('admin-connections.api.action.default_error'), { variant: 'error' });
      }
    },
    [actionConnectionRequest, closeDrawer, enqueueSnackbar, markAsRead, notification.id, t]
  );

  const renderAvatar = (
    <ListItemAvatar>
      {/* {notification.avatarUrl ? (
        <Avatar src={notification.avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : ( */}
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Box
          component="img"
          src="/assets/icons/notification/ic_mail.svg"
          sx={{ width: 24, height: 24 }}
        />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      primary={reader(notification.metadata.title ?? 'New Notification')}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          <Stack direction="column" justifyContent="start" spacing={1}>

            {_.capitalize(getNotificationSubtitle(notification))}

            <Typography variant='caption' color='text.secondary'>
              {fToNow(notification.created_at)}
            </Typography>
          </Stack>

        </Stack>
      }
    />
  );

  <Box
    sx={{
      top: 26,
      width: 8,
      height: 8,
      right: 20,
      borderRadius: '50%',
      bgcolor: 'info.main',
      position: 'absolute',
    }}
  />;

  const applicationRequestAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(
            setShowActionStageRequestModal(
              notificationObj.metadata?.variables?.request_id as string
            )
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const candidateOfferAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(
            showCandidateOfferModal(notificationObj.metadata?.variables?.offer_id as string)
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewCalendarEventAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(openCalendarEventViewer({
            eventId: notificationObj.metadata?.variables?.calendar_event_id as string
          }));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewDealAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(paths.dashboard.deal_flow.deals.detail(notificationObj.metadata?.variables?.deal_id as string));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewContactAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(paths.dashboard.contacts.detail(notificationObj.metadata?.variables?.contact_id as string));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewCompanyAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(paths.dashboard.companies.detail(notificationObj.metadata?.variables?.company_id as string, tenantType, notificationObj.metadata?.variables?.company_type as TenantType));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewOrgSettingsAction = (notificationObj: Notification, tab?: string) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(paths.dashboard.admin.organization.settings(tab));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const offerAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(
            setShowOfferModal({
              applicationId: notificationObj.metadata?.variables?.application_id as string,
              offerId: notificationObj.metadata?.variables?.offer_id as string,
            })
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const connectionAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        disabled={!isAdmin(authContext)}
        onClick={() =>
          onAcceptConnectionRequest(notification.metadata?.variables?.request_id as string)
        }
      >
        {t('common.accept')}
      </Button>
      <LoadingButton
        size="small"
        variant="outlined"
        disabled={!isAdmin(authContext)}
        onClick={() =>
          onRejectConnectionRequest(notification.metadata?.variables?.request_id as string)
        }
        loading={isRejectingConnection}
      >
        {t('common.decline')}
      </LoadingButton>
    </Stack>
  );

  const editJobAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.jobs.edit(notificationObj.metadata?.variables?.job_id as string)}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewJobAction = (notificationObj: Notification, jobId: string, tab?: string) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.jobs.detail(jobId, tab)}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewApplicationProfileAction = (notificationObj: Notification, jobId: string, applicationId: string) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          if (isOrganisationTenant(tenantType)) {
            router.push(
              `${paths.dashboard.jobs.application(
                jobId,
                applicationId
              )}?tab=profile`
            );
          } else if (jobId) {
            router.push(
              `${paths.dashboard.jobs.detail(
                jobId
              )}`
            );
          }
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const viewApplicationReferencesAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          if (isOrganisationTenant(tenantType)) {
            router.push(
              `${paths.dashboard.jobs.application(
                notificationObj.metadata?.variables?.job_id as string,
                notificationObj.metadata?.variables?.application_id as string
              )}?tab=references`
            );
          } else if (notificationObj.metadata?.variables?.job_id) {
            router.push(
              `${paths.dashboard.jobs.detail(
                notificationObj.metadata?.variables?.job_id as string
              )}`
            );
          }
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const jobAction = (notificationObj: Notification) => {
    if (
      [
        NotificationType.MASTER_JOB_UPDATED,
        NotificationType.JOB_CLIENT_VERIFIED,
        NotificationType.ROLE_CLOSED,
        NotificationType.ROLE_ON_HOLD
      ].includes(notification.type)) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              router.push(
                `${paths.dashboard.jobs.root}/${notificationObj.metadata?.variables?.job_id as string
                }`
              );
              closeDrawer();
              markAsRead({ notifications: [notification.id] });
            }}
          >
            {t('common.view')}
          </Button>
        </Stack>
      );
    }

    if (notification.type === NotificationType.JOB_LINK_REQUEST_INITIATED) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(
                showJobLinkActionModal({
                  requestId: notificationObj.metadata?.variables?.link_request_id as string,
                  notificationId: notification.id,
                })
              );

              closeDrawer();
            }}
          >
            {`${t('common.manage')}`}
          </Button>
        </Stack>
      );
    }

    if (notification.type === NotificationType.JOB_REQUIRES_APPROVAL) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(showJobVerificationModal());
              closeDrawer();
              markAsRead({ notifications: [notification.id] });
            }}
          >
            {`${t('common.manage')}`}
          </Button>
        </Stack>
      );
    }

    return null;
  };

  const jobInviteAction = (notificationObj: Notification) => {
    if (notification.type === NotificationType.JOB_INVITE_INITIATED) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(showCandidateJobRequestsModal());
              closeDrawer();
              markAsRead({ notifications: [notificationObj.id] });
            }}
          >
            {`${t('common.view')}`}
          </Button>
        </Stack>
      );
    }

    return null;
  };

  const interviewReviewAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(
            openInterviewActionModal({
              interviewId: notificationObj.metadata?.variables?.interview_id as string,
            })
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const candidateAvailabilityAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.candidates.root}/${notificationObj.metadata?.variables?.candidate_id as string
            }`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const openCompanyInviteAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(toggleCompanyInviteListModal({
            status: true
          }));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const openCandidateInviteListAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(toggleCandidateInviteListModal({
            status: true
          }));
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const viewCompaniesAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.companies.all}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const viewAgenciesAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.companies.recruiters}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const viewCandidateAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.candidates.root}/${notificationObj.metadata?.variables?.candidate_id as string}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const viewTasksActions = (notificationObj: Notification, tab: DueMarker = DueMarker.ALL) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          router.push(
            `${paths.dashboard.tasks.root(tab)}`
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {`${t('common.view')}`}
      </Button>
    </Stack>
  );

  const renderApplicationCategoryNotification = (notificationObj: Notification) => {
    if (notificationObj.metadata?.variables?.application_id) {
      // eslint-disable-next-line consistent-return
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (isOrganisationTenant(tenantType)) {
                router.push(
                  `${paths.dashboard.jobs.application(
                    notificationObj.metadata?.variables?.job_id as string,
                    notificationObj.metadata?.variables?.application_id as string
                  )}`
                );
              } else if (notificationObj.metadata?.variables?.job_id) {
                router.push(
                  `${paths.dashboard.jobs.detail(
                    notificationObj.metadata?.variables?.job_id as string
                  )}`
                );
              }
              closeDrawer();
              markAsRead({ notifications: [notificationObj.id] });
            }}
          >
            {t('common.view')}
          </Button>
        </Stack>
      );
    }

    if (!isOrganisationTenant(tenantType) && notificationObj.metadata?.variables?.job_id) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (isOrganisationTenant(tenantType))
                router.push(
                  `${paths.dashboard.jobs.detail(
                    notificationObj.metadata?.variables?.job_id as string
                  )}`
                );
              closeDrawer();
              markAsRead({ notifications: [notificationObj.id] });
            }}
          >
            {t('common.view')}
          </Button>
        </Stack>
      );
    }

    return null;
  };

  const renderInterviewNotification = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          dispatch(
            openInterviewActionModal({
              interviewId: notificationObj.metadata?.variables?.interview_id as string,
            })
          );
          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const renderThreadCommentAction = (notificationObj: Notification) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => {

          if (tenantType !== TenantType.Candidate) {
            if (notificationObj.metadata.variables?.metadata?.application_id) {
              router.push(
                `${paths.dashboard.jobs.application(
                  undefined,
                  notification.metadata.variables?.metadata.application_id as string,
                  "profile"
                )}#comment-list`
              );
            }
            else if (notificationObj.metadata.variables?.metadata?.job_id) {
              router.push(
                `${paths.dashboard.jobs.detail(
                  notification.metadata.variables?.metadata.job_id as string,
                  'content'
                )}#comment-list`
              );
            }
            else if (notificationObj.metadata.variables?.metadata?.contact_id) {
              router.push(
                `${paths.dashboard.contacts.detail(
                  notification.metadata.variables?.metadata.contact_id as string
                )}#comment-list`
              );
            }
            else if (notificationObj.metadata.variables?.metadata?.deal_id) {
              router.push(
                `${paths.dashboard.deal_flow.deals.detail(
                  notification.metadata.variables?.metadata.deal_id as string
                )}#comment-list`
              );
            }
            else if (notificationObj.metadata.variables?.metadata?.org_candidate_id) {
              router.push(
                `${paths.dashboard.candidates.detail(
                  notification.metadata.variables?.metadata.org_candidate_id as string
                )}#comment-list`
              );
            }
          }

          closeDrawer();
          markAsRead({ notifications: [notificationObj.id] });
        }}
      >
        {t('common.view')}
      </Button>
    </Stack>
  );

  const renderApplicationLink = (
    <Button
      size="small"
      variant="contained"
      onClick={() => {
        router.push(
          `${paths.dashboard.jobs.application(
            notification.metadata?.variables?.job_id as string,
            notification.metadata?.variables?.application_id as string
          )}`
        );
        closeDrawer();
        markAsRead({ notifications: [notification.id] });
      }}
    >
      {t('common.view')}
    </Button>
  );

  const renderAction = () => {
    // Notification Type specific actions are checked first.
    switch (notification.type) {
      case NotificationType.APPLICATION_OFFER_CHANGES_REQUESTED:
      case NotificationType.APPLICATION_OFFER_CREATED:
        return offerAction(notification);
      case NotificationType.APPLICATION_OFFER_EXTENDED:
        if (authContext.organization.type === TenantType.Candidate) {
          return candidateOfferAction(notification);
        }

        return offerAction(notification);

        break;
      case NotificationType.APPLICATION_STAGE_CHANGE_REQUEST_INITIATED:
        return applicationRequestAction(notification);
      case NotificationType.INITIATE_CONNECTION_REQUEST:
        return connectionAction;
      case NotificationType.JOB_INVITE_ACCEPTED:
        return renderApplicationLink;
      case NotificationType.JOB_CHANGES_REQUESTED:
        return editJobAction(notification);
      case NotificationType.INTERVIEW_IN_REVIEW:
        return interviewReviewAction(notification);
      case NotificationType.CANDIDATE_AVAILABILITY_UPDATED:
        return candidateAvailabilityAction(notification);
      case NotificationType.COMPANY_INVITE_INITIATED_REPRESENTED:
      case NotificationType.COMPANY_INVITE_INITIATED_UNREPRESENTED:
        return openCompanyInviteAction(notification);
      case NotificationType.COMPANY_INVITE_ACCEPTED:
        return viewAgenciesAction(notification);
      case NotificationType.CANDIDATE_INVITE_INITIATED:
        return openCandidateInviteListAction(notification);
      case NotificationType.CANDIDATE_INVITE_ACCEPTED:
        return viewCandidateAction(notification);
      case NotificationType.REFERENCE_REQUEST_REFEREES_PENDING:
        return viewJobAction(notification, notification.metadata?.variables?.job_id as string);
      case NotificationType.REFERENCE_REQUEST_COMPLETED:
        return viewApplicationReferencesAction(notification);
      case NotificationType.REFERENCE_REQUEST_APPROVAL_PENDING:
        return viewApplicationProfileAction(notification, notification.metadata?.variables?.job_id as string, notification.metadata?.variables?.application_id as string);
      case NotificationType.REFERENCE_REQUEST_REFEREE_SUBMITTED:
        return viewApplicationProfileAction(notification, notification.metadata?.variables?.job_id as string, notification.metadata?.variables?.application_id as string);
      case NotificationType.THREAD_COMMENT_CREATED:
      case NotificationType.THREAD_COMMENT_MENTION:
        return renderThreadCommentAction(notification);
      case NotificationType.DAILY_TASK_REMINDER:
        return viewTasksActions(notification, DueMarker.DUE_TODAY);
      case NotificationType.APPOINTMENT_BOOKING_RECEIVED:
        return viewCalendarEventAction(notification);
      case NotificationType.DEAL_ASSIGNED:
        return viewDealAction(notification);
      case NotificationType.CONTACT_ACCOUNT_OWNER_ASSIGNED:
        return viewContactAction(notification);
      case NotificationType.COMPANY_ACCOUNT_OWNER_ASSIGNED:
        return viewCompanyAction(notification);
      case NotificationType.EMAIL_JOB_FAILED:
        return viewOrgSettingsAction(notification, 'email-sync');
      case NotificationType.JOB_DOCUMENT_SHARED:
        return viewJobAction(notification, notification.metadata?.variables?.job_id as string, 'documents');
      case NotificationType.BACKGROUND_TASK_UPDATE:
        switch (notification.metadata.variables?.task_type) {
          case BackgroundTaskType.FINALIZE_APPLY_INTENT:
            if (notification.metadata?.variables?.completion_artifacts?.job?.id) {
              return viewJobAction(notification, notification.metadata?.variables?.completion_artifacts?.job?.id as string);
            }
            break;
          case BackgroundTaskType.IMPORT_CANDIDATE_PROFILE:

            break;
          case BackgroundTaskType.IMPORT_APPLICATION_PROFILE:
            if (notification.metadata?.variables?.task_data?.job_application?.job_id && notification.metadata?.variables?.task_data.job_application?.id) {
              return viewApplicationProfileAction(notification, notification.metadata?.variables?.task_data?.job_application?.job_id as string, notification.metadata?.variables?.task_data.job_application?.id as string);
            }
            break;
          default:
            break;
        }

        break;
      default:
        break;
    }

    switch (notification.category) {
      case NotificationCategory.JOB:
        return jobAction(notification);
      case NotificationCategory.JOB_INVITE:
        return jobInviteAction(notification);
      case NotificationCategory.JOB_APPLICATION:
        return renderApplicationCategoryNotification(notification);
      case NotificationCategory.INTERVIEW:
        return renderInterviewNotification(notification);
      default:
        return null;
    }
  };

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {/* {renderUnReadBadge} */}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }} gap={1} direction="row">
        {renderText}

        <Stack direction="column" justifyContent="space-between" alignItems="flex-end">
          <Tooltip title={t('notifications.popover.mark_read')}>
            <IconButton
              style={{
                width: 40,
                height: 40,
              }}
              size="medium"
              onClick={() => {
                markAsRead({ notifications: [notification.id] });
              }}
            >
              <Iconify icon="solar:check-read-line-duotone" />
            </IconButton>
          </Tooltip>

          {renderAction()}
        </Stack>
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
